import { Button, Modal } from '@employ-engage/common-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DashboardNameModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The name to start from. Empty when naming a new dashboard. */
  initialName?: string;
  title: string;
  confirmLabel: string;
  onConfirm: (name: string) => void;
}

/**
 * Naming a dashboard.
 *
 * A real field rather than `window.prompt`, which cannot be styled, cannot be
 * translated, and in some browsers is suppressed entirely — leaving a button
 * that appears to do nothing.
 */
export function DashboardNameModal({
  open,
  setOpen,
  initialName = '',
  title,
  confirmLabel,
  onConfirm,
}: DashboardNameModalProps) {
  const { t } = useTranslation(['common']);
  const [name, setName] = useState(initialName);

  // Reopening should offer what is true now, not what was typed last time.
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
    setOpen(false);
  };

  return (
    <Modal open={open} setOpen={setOpen} verticalAlign="top">
      <div className="space-y-4">
        <h2 className="text-base font-semibold text-light-primaryText">
          {title}
        </h2>
        <input
          autoFocus
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder={t('e.g. Safety overview')}
          className="block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button variant="primary" disabled={!name.trim()} onClick={submit}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DashboardNameModal;
